import { useCallback, useEffect, useState } from 'react';
import { useWebhookApi, Webhook } from '../api/useWebhookApi';

interface UseWebhooksOptions {
    /** Optional callback to save the graph before creating a webhook */
    saveGraph?: () => Promise<void>;
}

export const useWebhooks = (graphId: string, options: UseWebhooksOptions = {}) => {
    const { saveGraph } = options;
    const [webhooks, setWebhooks] = useState<Webhook[]>([]);
    const [nodeInstancesWithPendingRequests, setNodeInstancesWithPendingRequests] = useState<number[]>([]);

    const { createWebhook: createWebhookApi, getWebhooksForGraph } = useWebhookApi();

    useEffect(() => {
        const performEffect = async () => {
            try {
                const webhooks = await getWebhooksForGraph(graphId);
                setWebhooks(webhooks);
            } catch {}
        };
        performEffect();
    }, [getWebhooksForGraph, graphId]);

    const createWebhook = useCallback(
        async (nodeInstanceId: number) => {
            try {
                setNodeInstancesWithPendingRequests([...nodeInstancesWithPendingRequests, nodeInstanceId]);
                // Save the graph before creating the webhook to ensure the node exists
                if (saveGraph) {
                    await saveGraph();
                }
                const newWebhook = await createWebhookApi(graphId, nodeInstanceId);
                setWebhooks([...webhooks.filter(hook => hook.nodeInstanceId !== nodeInstanceId), newWebhook]);
            } catch {
            } finally {
                setNodeInstancesWithPendingRequests(nodeInstancesWithPendingRequests.filter(i => i !== nodeInstanceId));
            }
        },
        [createWebhookApi, nodeInstancesWithPendingRequests, graphId, webhooks, saveGraph],
    );

    return {
        webhooks,
        nodeInstancesWithPendingRequests,
        createWebhook,
    };
};
